<template>
  <div :class="wrapperClass">
    <slot name="loading" v-if="loading"></slot>
    <table v-else class="data-table w-full">
      <thead>
        <tr>
          <th
            v-for="(col, index) in normalizedColumns"
            :key="index"
            :style="col.headerStyle"
            :class="[col.headerClass, { sorted: sortKey === col.field }]"
            class="even-row"
            @click="
              !disableSorting &&
              col.field !== 'actions' &&
              col.field !== 'delete'
                ? sortColumn(col)
                : null
            "
          >
            <div class="header-content">
              <span>{{ col.header }}</span>
              <i
                v-if="
                  !disableSorting &&
                  sortKey === col.field &&
                  sortOrder == 'asc' &&
                  col.field !== 'actions' &&
                  col.field !== 'delete'
                "
                :class="sortIcons.asc"
              ></i>
              <i
                v-else-if="
                  !disableSorting &&
                  sortKey === col.field &&
                  sortOrder === 'desc' &&
                  col.field !== 'actions' &&
                  col.field !== 'delete'
                "
                :class="sortIcons.desc"
              ></i>
              <i
                v-else-if="
                  !disableSorting &&
                  col.field !== 'actions' &&
                  col.field !== 'delete'
                "
                class="sorting-icon ml-2"
              ></i>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in value"
          :key="getRowKey(row)"
          :class="{
            'row-disabled': rowDisabled ? rowDisabled(row) : false,
            'clickable-row': true,
            'even-row': rowIndex % 2 === 0,
            'odd-row': rowIndex % 2 !== 0,
          }"
        >
          <td
            v-for="(col, colIndex) in normalizedColumns"
            :key="colIndex"
            :class="[
              getCellClass(col),
              { 'text-center': isNumericColumn(col.field) },
            ]"
            @click="handleCellClick(col, row, rowIndex)"
          >
            <template v-if="$slots[col.field]">
              <slot :name="col.field" :data="row" :index="rowIndex" />
            </template>
            <template v-else-if="col.slotName && $slots[col.slotName]">
              <slot :name="col.slotName" :data="row" :index="rowIndex" />
            </template>
            <template v-else>
              <div
                v-if="col.field === 'companyName' && isDeleted"
                class="cell-content"
              >
                <span v-if="col.linkable" class="atlink">
                  {{ row[col.field] }}
                </span>
                <span v-else>
                  {{ row[col.field] }}
                </span>
              </div>
              <span
                v-else-if="col.field === 'delete'"
                class="delete-icon"
                @click.stop="handleTrashClick(row, rowIndex)"
                >
                <i :class="trashIcon"></i>
              </span>
              <span
                v-else-if="col.field === 'actions' && !hasDeleteColumn"
                class="dots-icon"
                @click.stop="toggleActionsDropdown(row, $event)"
              >
                <div
                  v-if="isActionsOpen && currentRow === row"
                  class="dropdown-menu"
                >
                  <div
                    v-for="(option, index) in splitValues"
                    :key="index"
                    class="dropdown-item"
                    @click.stop="handleAction(option.action, row)"
                  >
                    <i
                      v-if="option.icon"
                      :class="option.icon"
                      style="margin-right: 8px"
                    ></i>
                    {{ option.label }}
                  </div>
                </div>
              </span>
              <span v-else>
                {{ row[col.field] }}
              </span>
            </template>
          </td>
        </tr>
        <tr v-if="!value || value.length === 0">
          <td :colspan="normalizedColumns.length" class="no-data">
            <slot name="empty">No data available.</slot>
          </td>
        </tr>
      </tbody>
    </table>
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
  </div>
</template>

<script>
export default {
  name: "DataTableMiniVue",
  props: {
    id: { type: String, required: true, default: "" },
    value: { type: Array, required: true },
    selection: { type: [Array, Object], default: null },
    dataKey: { type: String, default: null },
    wrapperClass: { type: String, default: "table-wrapper" },
    rowDisabled: { type: Function, default: null },
    validation: { type: Object, default: null },
    PropName: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    rowClickRoute: { type: String, default: null },
    splitValues: { type: Array, default: () => [] },
    disableSorting: { type: Boolean, default: false },
    sortIcons: {
      type: Object,
      default: () => ({
        // asc: "fa fa-sort-up",
        // desc: "fa fa-sort-down",
        // neutral: "fa fa-sort",
      }),
    },
    isDeleted: { type: Boolean, default: false },
    multiSelectOff: { type: Boolean, default: false },
    trashIcon: { type: String, default: "trash-icon" },
  },
  emits: [
    "update:selection",
    "page",
    "sort",
    "row-click",
    "action-click",
    "trash-click",
  ],
  data() {
    return {
      selectedRows: this.selection || [],
      sortKey: null,
      sortOrder: "asc",
      isActionsOpen: false,
      currentRow: null,
    };
  },
  computed: {
    normalizedColumns() {
      const cols =
        this.$slots
          .default?.()
          .filter((vnode) => vnode.type?.name === "Column")
          .map((vnode) => {
            const props = vnode.props || {};
            return {
              field: props.field,
              header: props.header,
              sortable: props.sortable,
              selectionMode: this.multiSelectOff ? null : props.selectionMode,
              headerStyle: props.headerStyle,
              headerClass: props.headerClass,
              slotName: props.field,
              style: props.style,
              class: props.class,
              cellClass: props.cellClass,
              hasRowClick: !!props.onRowClick,
              linkable: props.linkable || false,
            };
          }) || [];
      return cols;
    },
    hasDeleteColumn() {
      return this.normalizedColumns.some((col) => col.field === "delete");
    },
    isAllSelected() {
      return (
        this.value.length > 0 && this.selectedRows.length === this.value.length
      );
    },
  },
  watch: {
    selectedRows(newSelection) {
      this.$emit("update:selection", newSelection);
    },
    selection(newSelection) {
      this.selectedRows = newSelection || [];
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector(".dropdown-menu");
      const dotsIcon = event.target.closest(".dots-icon");
      if (
        this.isActionsOpen &&
        dropdown &&
        !dropdown.contains(event.target) &&
        !dotsIcon
      ) {
        this.isActionsOpen = false;
        this.currentRow = null;
      }
    },
    getRowKey(row) {
      return this.dataKey ? row[this.dataKey] : row;
    },
    getCellClass(column) {
      return column.cellClass || "";
    },
    isNumericColumn(field) {
      return ["users", "sites", "deals"].includes(field);
    },
    handleCellClick(column, row, rowIndex) {
      if (column.hasRowClick && this.rowClickRoute && row.companyId) {
        try {
          this.$router.push({
            name: this.rowClickRoute,
            params: { id: row.companyId },
          });
          this.$emit("row-click", { row, rowIndex });
        } catch (error) {
          console.error("Navigation error:", error);
        }
      }
    },
    toggleAllSelection(event) {
      if (event.target.checked) {
        this.selectedRows = [...this.value];
      } else {
        this.selectedRows = [];
      }
    },
    updateSelection() {
      this.$emit("update:selection", this.selectedRows);
    },
    sortColumn(col) {
      if (this.disableSorting) return;

      if (this.sortKey === col.field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = col.field;
        this.sortOrder = "asc";
      }

      this.$emit("sort", {
        field: this.sortKey,
        order: this.sortOrder,
      });
    },
    toggleActionsDropdown(row, event) {
      event.stopPropagation();
      if (this.isActionsOpen && this.currentRow === row) {
        this.isActionsOpen = false;
        this.currentRow = null;
      } else {
        this.isActionsOpen = true;
        this.currentRow = row;
      }
    },
    handleAction(action, row) {
      if (action) {
        if (typeof action === "function") {
          action(row);
        } else {
          this.$emit("action-click", { row, action });
        }
      }
      this.isActionsOpen = false;
      this.currentRow = null;
    },
    handleTrashClick(row, rowIndex) {
      this.$emit("trash-click", { row, rowIndex });
    },
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
}
.data-table {
  border-collapse: collapse;
  font-family: "Voyado Text", sans-serif;
  font-size: 14px;
  color: #21211f;
  width: 100%;
  border: 1px solid #e2e2e2;
}
th {
  background-color: #f8f8f8;
  border-bottom: 1px solid #e2e2e2;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  max-width: 180px;
  overflow-wrap: break-word;
  cursor: pointer;
  white-space: nowrap;
}
td {
  border-bottom: 1px solid #e2e2e2;
  padding: 12px;
  text-align: left;
  color: #666;
  white-space: nowrap;
  position: relative;
}
.even-row {
  background-color: #fff;
}
.odd-row {
  background-color: #fff;
}
tr:hover {
  background-color: #fff;
}
.clickable-row {
  cursor: pointer;
}
.row-disabled {
  opacity: 0.6;
  pointer-events: none;
}
.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
}
.p-error {
  font-size: 14px;
  color: #dc3545;
}
td:first-child {
  padding-left: 16px;
}
.text-center {
  text-align: center;
}
.atlink {
  color: #388988;
  text-decoration: underline;
  cursor: pointer;
}
.atlink:hover {
  color: #388988;
  text-decoration: underline;
}
.actions-cell {
  text-align: center;
}
.actions-cell i {
  cursor: pointer;
  font-size: 16px;
  color: #666;
}
.actions-cell i:hover {
  color: #2d6e6d;
}
.sort-icon {
  margin-left: 5px;
  font-size: 12px;
  color: #666;
  display: inline-block;
  vertical-align: middle;
}
.sort-icon i {
  vertical-align: middle;
}
.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 150px;
  padding: 5px 0;
}
.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #333;
}
.dropdown-item:hover {
  background-color: #f5f5f5;
}
.header-content {
  display: flex;
  align-items: center;
  gap: 5px;
}
.cell-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.delete-icon {
  cursor: pointer;
  color: #ff6c60;
}
.delete-icon:hover {
  color: #ec6459;
}

</style>
